import { type ClassValue, clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

// ─── Date display ────────────────────────────────────────────────────────────
//
// The storage convention: date-only values are plain "YYYY-MM-DD" strings and
// mean a LOCAL calendar day; full ISO datetimes (created_at etc.) are UTC
// instants. `new Date("YYYY-MM-DD")` parses as UTC midnight, which renders as
// the previous day anywhere west of UTC — several views had hand-rolled
// formatters with exactly that bug. These two helpers are the one safe pair
// to use for display; they follow the same `+ 'T00:00:00'` pattern the
// scheduler code already uses.

/** True for a bare "YYYY-MM-DD" date-only string. */
const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;

/**
 * A stored date as a Date to READ local fields from: a plain "YYYY-MM-DD" is
 * that local day at midnight; anything else is parsed as the instant it is.
 *
 * Use it wherever a view builds a Date from a stored date to show its day,
 * weekday or month. `new Date('2026-10-05')` is UTC midnight — the evening of
 * the 4th in the Americas — which titled Week "Week of Oct 4" for a week
 * starting Monday the 5th and put every day number one too low.
 */
export function toLocalDate(value: string | Date): Date {
  if (typeof value !== 'string') return value;
  return new Date(DATE_ONLY.test(value) ? `${value}T00:00:00` : value);
}

/**
 * Format a stored date (date-only string, ISO datetime, or Date) as a local
 * calendar date, e.g. "Jul 30, 2026".
 */
export function formatDate(value: string | Date | null | undefined): string {
  if (!value) return '';
  const d = toLocalDate(value);
  if (isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

/**
 * Format an ISO datetime as local date + time, e.g. "Jul 30, 2026, 9:41 AM".
 * For created_at / updated_at / completed_at style instants.
 */
export function formatDateTime(value: string | Date | null | undefined): string {
  if (!value) return '';
  const d = toLocalDate(value);
  if (isNaN(d.getTime())) return '';
  return d.toLocaleString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
    hour: 'numeric', minute: '2-digit',
  });
}
